import { useEffect, useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { usePhase, useReadOnly, useStore } from "@/state/store";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { api } from "@/api/client";

/**
 * Rahmenfolien der Station 3 (Briefing Prompt-Teil, Abschnitt 5): E1/E2 vor
 * dem Thema, V1–V3 während der Generierung, A1 zum Abschluss. Texte sind
 * final und werden nicht umformuliert — Änderungen nur nach Freigabe.
 * Sprechernotizen stehen NICHT hier, sondern in offsite.config.ts
 * (sprechernotizen), damit sie nie auf der Leinwand landen.
 */

// ── Einstieg: Strategie 2030 (Wunsch 15.09.) ─────────────────────────────
//
// Der Stand heißt „Prozesse und Wachstum" — wer direkt mit dem Prompten
// einsteigt, verliert den Raum. Zwei Folien schlagen den Bogen: das Ziel
// (durchgehend digital bis 2030), dann die Werkzeuge (Self-Service für
// Kunden, Prozesse intern, KI als Beschleuniger) — und erst dann die Demo.

const S2_WERKZEUGE = [
  {
    title: "Für unsere Kunden",
    body: "Mehr selbst erledigen können — Anliegen, Verträge, Termine: jederzeit, ohne Warteschleife, ohne Papier.",
  },
  {
    title: "Für uns intern",
    body: "Abläufe mit viel Handarbeit erfassen und prüfen — ihr wisst, wie die Prozesse wirklich laufen. Genau da können wir gemeinsam automatisieren.",
  },
  {
    title: "Und die KI hilft dabei",
    body: "Sie baut aus einer Beschreibung in Minuten einen ersten Entwurf. Genau das probiert ihr jetzt selbst aus.",
  },
] as const;

export function SlideS1Strategie(_: SlideProps) {
  return (
    <div className="deck-root ar-slide ar-slide--hero" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Strategie 2030
        </div>
        <h1 className="deck-title rise rise-2">
          Wir gestalten die SVO bis 2030 durchgehend digital.
        </h1>
        <hr className="ar-rule rise rise-3" />
        <p className="deck-sub rise rise-4">
          Durchgehend heißt: intern wie im Kundenkontakt. Ob ein Anliegen
          von draußen kommt oder ein Ablauf bei uns im Haus läuft — der
          Prozess läuft digital durch, ohne Medienbrüche, ohne doppelte
          Erfassung, ohne Warten auf Papier.
        </p>
      </header>
    </div>
  );
}

export function SlideS2Werkzeuge(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Strategie 2030
        </div>
        <h1 className="deck-title rise rise-2">Womit machen wir das?</h1>
      </header>
      <div className="ar-statements">
        {S2_WERKZEUGE.map((w, i) => (
          <section key={w.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{w.title}</h2>
            <p className="ar-statement-body">{w.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}

// ── Einführung ───────────────────────────────────────────────────────────

/**
 * Erklärt schriftlich, was in der Session passiert — direkt auf der ersten
 * Folie, bewusst ohne Fachsprache (kein „Prompt", kein „generieren"), und
 * „Prototyp" wird im Satz selbst erklärt (abgestimmt 14.09.).
 */
/** E1 erklärt den Stand-Namen: was Prozesse & Wachstum mit KI und
 *  Digitalisierung zu tun haben (Wunsch 17.09.). */
const E1_SCHRITTE = [
  {
    title: "Prozesse",
    body: "Jeder Ablauf, der heute über Zettel, Postfächer und Abtippen läuft, ist ein Prozess. Digital kann er von allein durchlaufen.",
  },
  {
    title: "Wachstum",
    body: "Beratung, PV, Glasfaser, Netzausbau — da kommt Arbeit dazu. Die Zeit dafür holen wir aus der Routine.",
  },
  {
    title: "Und die KI?",
    body: "Sie verbindet beides: Aus einer Beschreibung in euren Worten macht sie in Minuten einen digitalen Entwurf. Entscheiden tut der Mensch.",
  },
] as const;

export function SlideE1(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Station 3
        </div>
        <h1 className="deck-title rise rise-2">Prozesse &amp; Wachstum</h1>
        <p className="deck-sub rise rise-3">
          Was hat das mit KI und Digitalisierung zu tun?
        </p>
      </header>
      <div className="ar-statements">
        {E1_SCHRITTE.map((s, i) => (
          <section key={s.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{s.title}</h2>
            <p className="ar-statement-body">{s.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}

/** E2 beantwortet: Was passiert jetzt — und warum? (Wunsch 17.09.). */
export function SlideE2(_: SlideProps) {
  return (
    <div className="deck-root ar-slide ar-slide--hero" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Gleich geht es los
        </div>
        <h1 className="deck-title rise rise-2">Was passiert jetzt — und warum?</h1>
        <hr className="ar-rule rise rise-3" />
        <p className="deck-sub rise rise-4">
          Ihr beschreibt in drei Feldern ein Werkzeug für euren Alltag — die KI
          baut daraus einen Prototyp. 8 Minuten, keine Vorkenntnisse, ein
          echtes Ergebnis, das euch gehört.
        </p>
        <p className="deck-sub rise rise-5">
          Warum? Weil ihr am besten wisst, wo es hakt. Eure Beschreibungen von
          heute sind der Startpunkt für echte Werkzeuge.
        </p>
      </header>
    </div>
  );
}

// ── Prompten: die drei Felder erklärt ────────────────────────────────────

const FELDER = [
  {
    title: "Was soll das Werkzeug sein?",
    body: "Ein Satz reicht. „Eine Liste, in der …“, „Eine Seite, auf der …“",
  },
  {
    title: "Was muss es können?",
    body: "Drei bis fünf Dinge, die es tun soll. So, wie du es einem Kollegen erklären würdest.",
  },
  {
    title: "Worauf kommt es an?",
    body: "Was dich heute nervt und morgen nicht mehr nerven soll.",
  },
] as const;

export function SlidePrompten(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Gleich geht es los
        </div>
        <h1 className="deck-title rise rise-2">Drei Felder</h1>
      </header>
      <div className="ar-statements">
        {FELDER.map((f, i) => (
          <section key={f.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{f.title}</h2>
            <p className="ar-statement-body">{f.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}

// ── Generierung: V1–V3 mit kleinem Bühnen-Fortschritt ────────────────────

/** Unten links, nur auf /present: „Prototypen 4 von 6 fertig". */
function BuehnenFortschritt() {
  const phase = usePhase();
  const status = useOvernightStatus(phase === "overnight" || phase === "reveal");
  if (!status || status.total === 0) return null;
  return (
    <p className="ar-v-fortschritt" data-testid="v-fortschritt">
      Prototypen {status.done} von {status.total} fertig
    </p>
  );
}

/**
 * Vortrag während der Generierung (V1–V3): eigenständige Inhalte, bewusst
 * KEINE Dopplung der Seitenbildschirm-Botschaften (die tragen die Zahlen).
 * Roter Faden: „Prozesse & Wachstum — was hat das mit uns und der KI zu
 * tun?", angeknüpft an das, was der Raum gerade selbst getan hat.
 */
const V1_PUNKTE = [
  {
    title: "Ihr habt beschrieben, was nervt",
    body: "Genau so beginnt jeder digitale Prozess bei uns: Jemand aus dem Alltag sagt, wo es hakt.",
  },
  {
    title: "Die KI baut daraus einen Entwurf",
    body: "Was früher Wochen Abstimmung brauchte, liegt in Minuten als klickbarer Vorschlag auf dem Tisch.",
  },
  {
    title: "Entscheiden tut der Mensch",
    body: "Ob aus dem Entwurf ein echtes Werkzeug wird, entscheiden Fachbereich und Kolleginnen — nicht die KI.",
  },
] as const;

export function SlideV1(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Prozesse &amp; Wachstum
        </div>
        <h1 className="deck-title rise rise-2">
          Was hat das mit uns und der KI zu tun?
        </h1>
        <p className="deck-sub rise rise-2">
          Das, was ihr gerade gemacht habt — im Großen.
        </p>
      </header>
      <div className="ar-statements">
        {V1_PUNKTE.map((p, i) => (
          <section key={p.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{p.title}</h2>
            <p className="ar-statement-body">{p.body}</p>
          </section>
        ))}
      </div>
      <BuehnenFortschritt />
    </div>
  );
}

/** Die Routine-Strecke zwischen Anruf und erledigt — übernimmt die KI. */
const ROUTINE_SCHRITTE = ["Zettel", "abtippen", "Rückfrage", "weiterleiten"] as const;

export function SlideV2(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Was übernimmt die KI?
        </div>
        <h1 className="deck-title rise rise-2">
          Ein Prozess ist der Weg bis „erledigt“.
        </h1>
      </header>
      {/* Anruf und Erledigt als feste Endpunkte, die Routine dazwischen als
       *  dezente Strecke mit KI-Klammer (Redesign 17.09. — vorher sechs
       *  gleichrangige Bubbles). */}
      <div className="prozess-strecke rise rise-3" aria-hidden="true">
        <span className="prozess-endpunkt">Anruf</span>
        <span className="prozess-mitte">
          <span className="prozess-mitte-schritte">
            {ROUTINE_SCHRITTE.map((s, i) => (
              <span key={s} className="prozess-mitte-schritt">
                {i > 0 && <span className="prozess-mitte-punkt">·</span>}
                {s}
              </span>
            ))}
          </span>
          <span className="prozess-mitte-linie" />
          <span className="prozess-mitte-badge">übernimmt die KI</span>
        </span>
        <span className="prozess-endpunkt">Erledigt</span>
      </div>
      <p className="deck-sub rise rise-4">
        Der Anruf und die Entscheidung bleiben bei euch — die Strecke
        dazwischen läuft künftig von allein.
      </p>
      <BuehnenFortschritt />
    </div>
  );
}

/* Beide Spalten auf derselben Flughöhe — knappe, parallele Tätigkeiten,
   je vier (Wunsch 17.09.). */
const WACHSTUM = [
  "Kunden beraten",
  "PV und Wärme anschließen",
  "Glasfaser ausbauen",
  "Netz erweitern",
] as const;

const ROUTINE_WENIGER = [
  "Zahlen abtippen",
  "Zettel weitertragen",
  "Status hinterhertelefonieren",
  "Doppelt erfassen",
] as const;

export function SlideV3(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Und das Wachstum?
        </div>
        <h1 className="deck-title rise rise-2">Nicht mehr Arbeit. Andere Arbeit.</h1>
        <p className="deck-sub rise rise-2">
          Die Zeit, die Routine kostet, geht dahin, wo Arbeit dazukommt — und
          eure Ideen von eben sind der Rohstoff dafür.
        </p>
      </header>
      {/* Zwei ruhige Spalten statt Bubbles (Redesign 17.09.): links, was
       *  weniger wird; rechts, was mehr wird — mit Akzentstrich wie auf den
       *  Seitenbildschirmen. */}
      <div className="wandel-grid rise rise-3">
        <section className="wandel-spalte is-weniger">
          <h2 className="wandel-titel">Wird weniger</h2>
          <ul className="wandel-liste">
            {ROUTINE_WENIGER.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </section>
        <section className="wandel-spalte is-mehr">
          <h2 className="wandel-titel">Wird mehr</h2>
          <ul className="wandel-liste">
            {WACHSTUM.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </section>
      </div>
      <BuehnenFortschritt />
    </div>
  );
}

// ── Vortrag: T1–T3 — Transparenz und Aufbruch ────────────────────────────

/** Was mit den Ideen passiert — Transparenz statt Blackbox. */
const T1_PUNKTE = [
  {
    title: "Alles wird gesichert",
    body: "Jede Einreichung von heute wird nach dem Strategietag gesichtet — nichts verschwindet in einem Ordner.",
  },
  {
    title: "Die stärksten gehen weiter",
    body: "Mit dem Fachbereich, der die Arbeit kennt: prüfen, schärfen, umsetzen. Vom Prototyp zum Werkzeug.",
  },
  {
    title: "Euer Link bleibt",
    body: "Jede und jeder behält den eigenen Prototyp — und sieht, was aus der Idee geworden ist.",
  },
] as const;

export function SlideT1(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Transparenz
        </div>
        <h1 className="deck-title rise rise-2">Was passiert mit euren Ideen?</h1>
      </header>
      <div className="ar-statements">
        {T1_PUNKTE.map((p, i) => (
          <section key={p.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{p.title}</h2>
            <p className="ar-statement-body">{p.body}</p>
          </section>
        ))}
      </div>
      <BuehnenFortschritt />
    </div>
  );
}

/** Leitplanken — was die KI bei uns nicht tut. Ehrlichkeit nimmt Angst. */
const T2_PUNKTE = [
  {
    title: "Keine Entscheidung über Menschen",
    body: "Die KI schlägt vor und sortiert vor — entschieden wird von Menschen, die die Verantwortung tragen.",
  },
  {
    title: "Erst testen, dann echte Daten",
    body: "Alles, was heute entsteht, läuft mit Beispieldaten. Mit echten Kundendaten arbeitet nur, was geprüft und abgesichert ist.",
  },
  {
    title: "Sie schafft Zeit fürs Wichtige",
    body: "Die KI räumt die Routine weg — damit mehr Zeit bleibt für den Anruf, den Termin am Zähler, die Entscheidung am Schacht.",
  },
] as const;

export function SlideT2(_: SlideProps) {
  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Leitplanken
        </div>
        <h1 className="deck-title rise rise-2">Was die KI bei uns nicht tut</h1>
      </header>
      <div className="ar-statements">
        {T2_PUNKTE.map((p, i) => (
          <section key={p.title} className={`ar-statement rise rise-${i + 3}`}>
            <h2 className="ar-statement-title">{p.title}</h2>
            <p className="ar-statement-body">{p.body}</p>
          </section>
        ))}
      </div>
      <BuehnenFortschritt />
    </div>
  );
}

export function SlideT3(_: SlideProps) {
  return (
    <div className="deck-root ar-slide ar-slide--hero" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Aufbruch
        </div>
        <h1 className="deck-title t-display rise rise-2">Heute war der Anfang.</h1>
        <hr className="ar-rule rise rise-3" />
        <p className="deck-sub rise rise-4">
          Aus euren Beschreibungen sind gerade echte Prototypen geworden —
          Schritt für Schritt, Werkzeug für Werkzeug. Schauen wir sie uns an.
        </p>
      </header>
      <BuehnenFortschritt />
    </div>
  );
}

// ── Abschluss: A1 mit Zählern ────────────────────────────────────────────

/**
 * Abschluss: Aufbruch statt Zahlenschau (Entscheidung 14.09.) — die
 * Kernbotschaft ist „die KI arbeitet für euch", die Tageszahl läuft nur als
 * Beleg im Nebensatz mit (aus dem öffentlichen /phase-Vertrag, alle 5 s).
 */
export function SlideA1(_: SlideProps) {
  const [heute, setHeute] = useState<number | null>(null);

  useEffect(() => {
    let aktiv = true;
    const laden = async () => {
      try {
        const k = await api.phase.kontrakt();
        if (aktiv) setHeute(k?.counts?.submittedToday ?? null);
      } catch {
        /* Folie funktioniert auch ohne Zahl. */
      }
    };
    void laden();
    const t = setInterval(() => void laden(), 5000);
    return () => {
      aktiv = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div
      className="deck-root ar-slide ar-slide--hero abschluss-grid"
      style={{ background: "var(--ink-900)" }}
    >
      <div className="deck-bg" aria-hidden="true" />
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Abschluss
        </div>
        <h1 className="deck-title t-display rise rise-2">
          Ihr gestaltet die digitale SVO mit.
        </h1>
        <hr className="ar-rule rise rise-3" />
        <p className="deck-sub rise rise-4">
          Bis 2030 gestalten wir die SVO durchgängig digital — damit Prozesse,
          die euch bremsen oder unnötig Zeit kosten, mit technischer
          Unterstützung einfacher werden oder im Hintergrund verschwinden.
        </p>
        <p className="deck-sub rise rise-5">
          {heute !== null && heute > 0 && (
            <>Heute habt ihr gezeigt, wie das geht: {heute} Werkzeuge an einem Tag. </>
          )}
          Euer Prototyp bleibt unter eurem Link.
        </p>
        <p className="deck-sub rise rise-6">
          Und wenn noch Zeit ist: Welcher Prozess soll als Nächstes dran sein?
          Sagt es uns — jetzt, oder gerne auch nachher bei einem Kaffee.
        </p>
      </header>
      <AbschlussIdeen />
    </div>
  );
}

/**
 * Zurufe aus dem Publikum, live von der Moderation eingetippt (Wunsch
 * 17.09.): Eingabefeld + Absenden, die Liste wächst auf der Folie mit.
 * Gespeichert wird serverseitig (überlebt Gruppenwechsel und Reload);
 * angezeigt werden die letzten sechs, neueste zuerst.
 */
function AbschlussIdeen() {
  const { state } = useStore();
  const token = state.auth.token;
  const readOnly = useReadOnly();
  const [text, setText] = useState("");
  const [ideen, setIdeen] = useState<
    { id: string; text: string; createdAt: number }[]
  >([]);
  const [begriffe, setBegriffe] = useState<{ text: string; gewicht: number }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let aktiv = true;
    const laden = async () => {
      try {
        const r = await api.admin.ideenListe(token);
        if (aktiv) setIdeen(r.ideen);
      } catch {
        /* Folie funktioniert auch ohne Liste. */
      }
    };
    // Die Wortwolke rechnet Haiku aus allen Zurufen — serverseitig gecacht,
    // neu nur wenn Ideen dazugekommen sind. 15s reichen für „live".
    const wolke = async () => {
      try {
        const r = await api.admin.ideenAggregat(token);
        if (aktiv && r.begriffe.length > 0) setBegriffe(r.begriffe);
      } catch {
        /* ohne Wolke zeigen wir einfach die rohen Zurufe */
      }
    };
    void laden();
    void wolke();
    const t = setInterval(() => void laden(), 5000);
    const w = setInterval(() => void wolke(), 15000);
    return () => {
      aktiv = false;
      clearInterval(t);
      clearInterval(w);
    };
  }, [token]);

  const absenden = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await api.admin.ideeAbsenden(token, t);
      setIdeen((alt) => [{ ...r.idee }, ...alt]);
      setText("");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const zuruecksetzen = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.admin.ideenReset(token);
      setIdeen([]);
      setBegriffe([]);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (readOnly) return null;

  const maxGewicht = Math.max(1, ...begriffe.map((b) => b.gewicht));

  return (
    <div className="abschluss-ideen rise rise-6">
      <form
        className="abschluss-ideen-form"
        onSubmit={(e) => {
          e.preventDefault();
          void absenden();
        }}
      >
        <input
          type="text"
          className="abschluss-ideen-feld"
          data-testid="idee-feld"
          placeholder="Zuruf aus dem Publikum …"
          value={text}
          maxLength={200}
          onChange={(e) => setText(e.target.value)}
        />
        <button
          type="submit"
          className="abschluss-ideen-knopf"
          data-testid="idee-absenden"
          disabled={!text.trim() || busy}
        >
          {busy ? "…" : "Absenden"}
        </button>
      </form>
      {begriffe.length > 0 && (
        <div className="abschluss-wolke" data-testid="ideen-wolke" aria-hidden="true">
          {begriffe.map((b) => (
            <span
              key={b.text}
              className="abschluss-wolke-begriff"
              style={{
                fontSize: `calc(var(--t-body) * ${(0.8 + 0.9 * (b.gewicht / maxGewicht)).toFixed(2)})`,
                opacity: 0.6 + 0.4 * (b.gewicht / maxGewicht),
              }}
            >
              {b.text}
            </span>
          ))}
        </div>
      )}
      {ideen.length > 0 && (
        <ul className="abschluss-ideen-liste" data-testid="ideen-liste">
          {ideen.slice(0, 5).map((i) => (
            <li key={i.id}>{i.text}</li>
          ))}
        </ul>
      )}
      {(ideen.length > 0 || begriffe.length > 0) && (
        <button
          type="button"
          className="abschluss-ideen-reset"
          data-testid="ideen-reset"
          onClick={() => void zuruecksetzen()}
          disabled={busy}
          title="Blendet die Zurufe für die nächste Gruppe aus — gespeichert bleiben sie alle"
        >
          ↺ Wolke zurücksetzen
        </button>
      )}
    </div>
  );
}
